import { faArrowLeft, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import type { ChatRoomNode, ChatRoomUser } from '../types';

interface ChatHeaderProps {
  chatRoom?: ChatRoomNode | null;
  currentUserId?: string;
  unreadCount: number;
  onToggle: () => void;
  onBack?: () => void;
  isExpanded: boolean;
  showBackButton?: boolean;
}

const ChatHeaderRoot = styled('div')(({ theme }) => ({
  padding: theme.baseUnit * 2,
  backgroundColor: theme.colors.alias.primary,
  color: theme.colors.alias.textContrast,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
}));

const ChatHeaderInfo = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
});

const ChatHeaderText = styled('div')(({ theme }) => ({
  marginLeft: theme.baseUnit,
}));

const CoachName = styled(Typography)(({ theme }) => ({
  ...theme.typography.fontFamily.sharp600,
  fontSize: 14,
  color: theme.colors.alias.textContrast,
}));

const BackButton = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 44,
  height: 44,
  borderRadius: '50%',
  cursor: 'pointer',
  color: theme.colors.alias.textContrast,
  marginRight: theme.baseUnit / 2,
  marginLeft: -theme.baseUnit / 2,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  '&:active': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
}));

const ExpandButton = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: '50%',
  cursor: 'pointer',
  color: theme.colors.alias.textContrast,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
}));

const ChatHeader: React.FC<ChatHeaderProps> = ({
  chatRoom,
  currentUserId,
  unreadCount: _unreadCount,
  onToggle,
  onBack,
  isExpanded,
  showBackButton = false,
}) => {
  const getDisplayName = () => {
    if (!chatRoom) {
      return 'Chats';
    }

    if (chatRoom.kind === 'direct') {
      const otherUser = chatRoom.usersWithoutInput.nodes.find(
        (user: ChatRoomUser) => user.id !== currentUserId,
      );
      return otherUser
        ? `${otherUser.firstName} ${otherUser.lastName}`
        : 'Direct Chat';
    }

    return chatRoom.name || 'Group Chat';
  };

  const getDisplayAvatar = () => {
    if (!chatRoom) {
      return undefined;
    }

    if (chatRoom.kind === 'direct') {
      const otherUser = chatRoom.usersWithoutInput.nodes.find(
        (user: ChatRoomUser) => user.id !== currentUserId,
      );
      return otherUser?.avatar?.preview ?? undefined;
    }

    return undefined;
  };

  const displayName = getDisplayName();

  return (
    <ChatHeaderRoot
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle();
        }
      }}
    >
      <ChatHeaderInfo>
        {showBackButton && onBack && (
          <BackButton
            role="button"
            aria-label="Back to chat list"
            onClick={(e) => {
              e.stopPropagation();
              onBack();
            }}
          >
            <FontAwesomeIcon icon={faArrowLeft} size="sm" />
          </BackButton>
        )}
        {chatRoom && (
          <Avatar
            src={getDisplayAvatar()}
            alt={displayName}
            sx={{ width: 32, height: 32 }}
          >
            {displayName.charAt(0)}
          </Avatar>
        )}
        <ChatHeaderText>
          <CoachName>{displayName}</CoachName>
        </ChatHeaderText>
      </ChatHeaderInfo>
      <ExpandButton>
        <FontAwesomeIcon
          icon={faChevronDown}
          style={{
            transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
            transition: 'transform 0.2s ease',
          }}
        />
      </ExpandButton>
    </ChatHeaderRoot>
  );
};

export default ChatHeader;
